import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, HelpCircle } from 'lucide-react';

interface ConfirmOptions {
    title: string;
    message?: React.ReactNode;
    /** Text på bekräftelseknappen — säg vad som händer ("Radera permanent"), inte "OK". */
    confirmLabel: string;
    cancelLabel?: string;
    /** danger = röd knapp för det som inte går att ångra. */
    tone?: 'default' | 'danger';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Bekräftelse i appens egen stil, i stället för webbläsarens gråa confirm().
 * Används bara för det som inte går att ångra — allt annat görs direkt med
 * en ångra-notis.
 */
export const useConfirm = (): ConfirmFn => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
    return ctx;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [request, setRequest] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    const confirm = useCallback<ConfirmFn>(
        options => new Promise<boolean>(resolve => setRequest({ ...options, resolve })),
        []
    );

    const close = (ok: boolean) => {
        request?.resolve(ok);
        setRequest(null);
    };

    useEffect(() => {
        if (!request) return;
        // Fokus på det säkra valet vid radering, annars på bekräftelsen.
        (request.tone === 'danger' ? cancelRef : confirmRef).current?.focus();
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

    const danger = request?.tone === 'danger';

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {request && createPortal(
                <div className="fixed inset-0 z-[2100] flex items-end sm:items-center justify-center p-4">
                    <div className="absolute inset-0 bg-scrim/50 backdrop-blur-sm" onClick={() => close(false)} />
                    <div
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="confirm-title"
                        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl ring-1 ring-line-soft p-5 animate-fade-in-up"
                    >
                        <div className="flex gap-3.5">
                            <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${danger ? 'bg-tone-rust-soft text-tone-rust' : 'bg-paper-100 text-ink-700'}`} aria-hidden="true">
                                {danger ? <AlertTriangle size={18} /> : <HelpCircle size={18} />}
                            </span>
                            <div className="min-w-0">
                                <h2 id="confirm-title" className="text-title font-bold text-ink-900">{request.title}</h2>
                                {request.message && <div className="text-body text-ink-700 mt-1">{request.message}</div>}
                            </div>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
                            <button
                                ref={cancelRef}
                                type="button"
                                onClick={() => close(false)}
                                className="h-11 px-4 rounded-xl border border-line bg-white text-meta font-bold text-ink-700 hover:bg-paper-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coffee-600/40"
                            >
                                {request.cancelLabel || 'Avbryt'}
                            </button>
                            <button
                                ref={confirmRef}
                                type="button"
                                onClick={() => close(true)}
                                className={`h-11 px-4 rounded-xl text-meta font-bold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                    danger ? 'bg-tone-rust hover:bg-tone-rust/90 focus-visible:ring-tone-rust/50' : 'bg-coffee-900 hover:bg-ink-950 focus-visible:ring-coffee-600/60'
                                }`}
                            >
                                {request.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </ConfirmContext.Provider>
    );
};
